import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Pagination } from 'react-bootstrap';
import APIserver from '../../../../api/axiosConfig';
import { Link } from 'react-router-dom';

const ReporteIngresos = () => {
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [totalIngresos, setTotalIngresos] = useState({ numeroPagos: 0, totalIngresos: 0 });
    const [data, setData] = useState([]); // Datos de los bancos
    const [currentPageBancos, setCurrentPageBancos] = useState(1); // Paginación de bancos
    const [rowsPerPageBancos, setRowsPerPageBancos] = useState(10);
    const [currentPagePagos, setCurrentPagePagos] = useState(1); // Paginación de pagos
    const [rowsPerPagePagos, setRowsPerPagePagos] = useState(10);
    const [totalPagesBancos, setTotalPagesBancos] = useState(1); // Total de páginas para bancos
    const [totalPagesPagos, setTotalPagesPagos] = useState(1); // Total de páginas para pagos
    const [totalRecords, setTotalRecords] = useState(0); // Estado para el total de registros
    const [pagos, setPagos] = useState([]); // Estado para almacenar los pagos recibidos

    useEffect(() => {
        const today = new Date();

        // Fecha de inicio del mes (primer día)
        const primerDiaMes = new Date(today.getFullYear(), today.getMonth(), 1);
        const fechaInicioMes = primerDiaMes.toISOString().split('T')[0]; // Formato YYYY-MM-DD

        // Fecha de fin del mes (último día)
        const ultimoDiaMes = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const fechaFinMes = ultimoDiaMes.toISOString().split('T')[0];

        setFechaInicio(fechaInicioMes);
        setFechaFin(fechaFinMes);
    }, []);

    const fetchReportes = async () => {
        try {
            // Realizamos la solicitud al servidor
            const totalIngresosRes = await APIserver.get('/api/reportesFinancieros/total-ingresos', {
                params: { fechaInicio, fechaFin }, // Pasamos las fechas como parámetros de consulta
            });

            // Validamos y actualizamos el estado con los datos obtenidos
            if (totalIngresosRes.data) {
                setTotalIngresos(totalIngresosRes.data);
            } else {
                setTotalIngresos({ numeroPagos: 0, totalIngresos: 0 });
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
        }
    };

    useEffect(() => {
        if (fechaInicio && fechaFin) { // Asegúrate de que ambas fechas estén seleccionadas
            fetchReportes();
        }
    }, [fechaInicio, fechaFin]); // Cierre correcto del array de dependencias

    const exportarReportes = async () => {
        try {
            const response = await APIserver.get('/api/reportesFinancieros/exportar-reportes', {
                params: { fechaInicio, fechaFin },
                responseType: 'blob', // Especificamos que la respuesta será un archivo
            });

            // Crear un enlace para descargar el archivo Excel
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'reportes_financieros.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error al descargar los reportes:', error);
        }
    };

    // Función para formatear valores en COP
    const formatCOP = (value) => value?.toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }) || '$0';

    const fetchData = async (page = 1, limit = 10) => {
        try {
            const response = await APIserver.get('api/reportesFinancieros/total-montos-bancos', {
                params: {
                    fechaInicio,
                    fechaFin,
                    page,
                    limit,
                },
            });

            setData(response.data.data); // Los datos de los bancos
            setTotalRecords(response.data.totalCount); // El total de registros
            setTotalPagesBancos(Math.ceil(response.data.totalCount / limit)); // Calculamos las páginas totales
        } catch (error) {
            console.error('Error al obtener los datos:', error);
        }
    };

    useEffect(() => {
        if (fechaInicio && fechaFin) {
            fetchData(currentPageBancos, rowsPerPageBancos);
        }
    }, [currentPageBancos, rowsPerPageBancos, fechaInicio, fechaFin]);

    const fetchPagos = async () => {
        try {
            const response = await APIserver.get(`/api/reportesFinancieros/total-pagos`, {
                params: {
                    fechaInicio,
                    fechaFin,
                    limit: rowsPerPagePagos, // Usa el estado correcto
                    offset: (currentPagePagos - 1) * rowsPerPagePagos, // Usa el estado correcto
                }
            });
            const { results, totalPages } = response.data;
            setPagos(results);
            setTotalPagesPagos(totalPages); // Usar el totalPages que ahora viene del backend
        } catch (error) {
            console.error('Error al obtener los pagos:', error);
        }
    };

    useEffect(() => {
        if (fechaInicio && fechaFin) {
            fetchPagos();
        }
    }, [currentPagePagos, rowsPerPagePagos, fechaInicio, fechaFin]);

    const handlePageChangeBancos = (page) => {
        setCurrentPageBancos(page);
        fetchData(page, rowsPerPageBancos); // Llamada con los estados específicos de Bancos
    };

    const handlePageChangePagos = (page) => {
        setCurrentPagePagos(page);
        fetchPagos(); // Llamada con los estados específicos de Pagos
    };

    const handleChangeRowsPerPageBancos = (e) => {
        setRowsPerPageBancos(parseInt(e.target.value, 10));
        setCurrentPageBancos(1); // Reinicia a la primera página de Bancos
    };

    const handleChangeRowsPerPagePagos = (e) => {
        setRowsPerPagePagos(parseInt(e.target.value, 10));
        setCurrentPagePagos(1); // Reinicia a la primera página de Pagos
    };

    // Función para formatear la fecha en formato DD/MM/YYYY
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Los meses son de 0 a 11
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <Container fluid className="py-3">
            {/* Filtros de Reporte */}
            <div className='d-flex flex-column flex-md-row align-items-center align-items-md-end justify-content-center mb-3 text-center'>
                <div className='d-flex flex-column me-md-3 mb-3 mb-md-0'>
                    <Form.Label>Desde</Form.Label>
                    <Form.Control
                        type='date'
                        value={fechaInicio}
                        onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (new Date(selectedDate) <= new Date(fechaFin || new Date())) {
                                setFechaInicio(selectedDate);
                            }
                        }}
                        max={new Date().toISOString().split('T')[0]} // Fecha máxima: Hoy
                        style={{ minWidth: '200px' }}
                    />
                </div>
                <div className='d-flex flex-column me-md-3 mb-3 mb-md-0'>
                    <Form.Label>Hasta</Form.Label>
                    <Form.Control
                        type='date'
                        value={fechaFin}
                        onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (new Date(selectedDate) >= new Date(fechaInicio) && new Date(selectedDate) <= new Date()) {
                                setFechaFin(selectedDate);
                            }
                        }}
                        max={new Date().toISOString().split('T')[0]} // Fecha máxima: Hoy
                        style={{ minWidth: '200px' }}
                    />
                </div>
                <div className='d-flex align-items-center justify-content-end mt-3 mt-md-0'>
                    <Button variant='danger' onClick={exportarReportes}>
                        Exportar
                    </Button>
                </div>
            </div>
            <Row className="mb-4 text-center">
                <Col md={3}>
                    <Card bg='light' className='text-dark'>
                        <Card.Body>
                            <Card.Title>Total Ingresos {totalIngresos.numeroPagos || 0}</Card.Title>
                            <h4>{formatCOP(totalIngresos.totalIngresos)}</h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col>
                    <div style={{ boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.1)' }}>
                        <Table responsive className='text-center align-middle border'>
                            <thead>
                                <tr>
                                    <th colSpan={2}>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div>
                                                <Form.Select
                                                    className='ms-2'
                                                    value={rowsPerPageBancos}
                                                    onChange={handleChangeRowsPerPageBancos}
                                                >
                                                    <option value='5'>5</option>
                                                    <option value='10'>10</option>
                                                    <option value='20'>20</option>
                                                    <option value='50'>50</option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Banco</th>
                                    <th>Monto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, index) => (
                                    <tr key={index}>
                                        <td>{row.banco}</td>
                                        <td>{formatCOP(row.totalMonto)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2}>
                                        <div className='d-flex justify-content-center align-items-center'>
                                            <Pagination>
                                                {Array.from({ length: totalPagesBancos }, (_, i) => (
                                                    <Pagination.Item
                                                        key={i + 1}
                                                        active={i + 1 === currentPageBancos}
                                                        onClick={() => handlePageChangeBancos(i + 1)}
                                                    >
                                                        {i + 1}
                                                    </Pagination.Item>
                                                ))}
                                            </Pagination>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Col>
            </Row>
            <div style={{ boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.1)" }}>
                <Table responsive className='text-center align-middle border'>
                    <thead>
                        <tr>
                            <th colSpan={7}>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <div>
                                        <Form.Select
                                            className='ms-2'
                                            value={rowsPerPagePagos}
                                            onChange={handleChangeRowsPerPagePagos}
                                        >
                                            <option value='5'>5</option>
                                            <option value='10'>10</option>
                                            <option value='20'>20</option>
                                            <option value='50'>50</option>
                                        </Form.Select>
                                    </div>
                                </div>
                            </th>
                        </tr>
                        <tr>
                            <th>Pago</th>
                            <th>Factura</th>
                            <th>Cliente</th>
                            <th>Observaciones</th>
                            <th>Creacion</th>
                            <th>Cuenta</th>
                            <th>Monto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagos.map((pago) => (
                            <tr key={pago.idPago}>
                                <td>
                                    <Link to={`/admin/PagosRecibidos/DetallesPago/${pago.idPago}/`} className='text-decoration-none'>
                                        {pago.numPagoRe}
                                    </Link>
                                </td>
                                <td>
                                    <Link to={`/admin/Facturas/DetallesFactura/${pago.idFacV}/`} className='text-decoration-none'>
                                        {pago.numFacV}
                                    </Link>
                                </td>
                                <td>
                                    <Link to={`/admin/Clientes/DetallesClientes/${pago.idCliente}/`} className='text-decoration-none'>
                                        {pago.nomCliente}
                                    </Link>
                                </td>
                                <td>{pago.detalle}</td>
                                <td>{formatDate(pago.fecha)}</td>
                                <td>
                                    <Link to={`/admin/Bancos/DetallesBancos/${pago.idBanco}/`} className='text-decoration-none'>
                                        {pago.nomBanco}
                                    </Link>
                                </td>
                                <td>{formatCOP(pago.monto)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={7}>
                                <div className='d-flex justify-content-center align-items-center'>
                                    <Pagination>
                                        {Array.from({ length: totalPagesPagos }, (_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={i + 1 === currentPagePagos}
                                                onClick={() => handlePageChangePagos(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}
                                    </Pagination>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </Container >
    );
};

export default ReporteIngresos;
